import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { FlexLayout, GridLayout } from '@Styled/utilities';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useImmer } from 'use-immer';
import CourseCompletionSelect from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/RolesPaths/Components/CourseCompletionSelect';
import PrerequisiteCriteria from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/RolesPaths/Components/PrerequisiteCriteria';
import PrerequisitePath from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/RolesPaths/Components/PrerequisitePath';
import ProgressPercentage from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/RolesPaths/Components/ProgressPercentage';
import UnitOrderSelect from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/RolesPaths/Components/UnitOrderSelect';
import UnitSelect from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/RolesPaths/Components/UnitSelect';
import { ROLES_PATHS_SCHEMA } from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/RolesPaths/Schema/RolesPaths.schema';
import {
	MultiSelectOptions,
	RolesPathsTypes,
} from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/RolesPaths/types/RolesPaths.types';
import useStandalonePageLoader from './useStandalonePageLoader';
import useAdminActionPermissions from './useAdminActionPermissions';

type LectureUnit = {
	id: number;
	name: string;
	progress_included: boolean;
};

type LectureRules = {
	id: number;
	name: string;
	units: LectureUnit[];
	progress_criteria?: string;
	progress_percentage?: number | null;
	sequential_units?: boolean;
	prerequisite_criteria?: string;
	prerequisites_paths?: number[][];
};

type LectureOption = { id: number; name: string };

const errorMessage = (error: any, fallback: string) =>
	error.response?.data?.message || error.data?.message || fallback;

const StandaloneLectureRulesPaths: React.FC<{
	standaloneCourseId: number;
	standaloneCourseLabel: string;
	lectureId: number;
}> = ({ standaloneCourseId, standaloneCourseLabel, lectureId }) => {
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	// Rules authoring is admin-only — the instructor portal is view-only here
	const { isAdminPortal } = useAdminActionPermissions();
	const readOnly = !isAdminPortal;
	const [lecture, setLecture] = useState<LectureRules>();
	const [lectureOptions, setLectureOptions] = useState<LectureOption[]>([]);
	const [prerequisitePaths, setPrerequisitePaths] = useImmer<
		MultiSelectOptions[]
	>([]);
	const [checked, setChecked] = useState<boolean | undefined>();
	const [clicked, setClicked] = useState<boolean | undefined>();

	const methods = useForm<RolesPathsTypes>({
		resolver: yupResolver(ROLES_PATHS_SCHEMA),
		mode: 'all',
		defaultValues: {
			course_id: lectureId,
			units: [],
			progress_criteria: 'all',
			progress_percentage: undefined,
			sequential_units: true,
			prerequisite_criteria: 'purchase',
			prerequisites: [],
		},
	});
	const resetForm = methods.reset;

	const load = useCallback(async () => {
		try {
			await withLoading(async () => {
				const [lectureResponse, optionsResponse] = await Promise.all([
					api.post('standalone-courses/lectures', { id: lectureId }),
					api.post('standalone-courses/lectures/all', {
						standalone_course_id: standaloneCourseId,
						page: 1,
						perPage: 1000,
						filters: [],
						query: '',
						sortBy: { field: 'order', direction: 'asc' },
					}),
				]);
				const nextLecture = lectureResponse.data.data as LectureRules;
				const nextOptions = (optionsResponse.data.data as LectureOption[]).filter(
					(option) => option.id !== lectureId
				);
				setLecture(nextLecture);
				setLectureOptions(nextOptions);
				resetForm({
					course_id: lectureId,
					units: nextLecture.units
						.filter((unit) => unit.progress_included)
						.map((unit) => unit.id),
					progress_criteria: nextLecture.progress_criteria || 'all',
					progress_percentage: nextLecture.progress_percentage || undefined,
					sequential_units: Boolean(nextLecture.sequential_units),
					prerequisite_criteria:
						nextLecture.prerequisite_criteria || 'purchase',
					prerequisites: [],
				});
				setPrerequisitePaths((draft) => {
					draft.splice(0, draft.length);
					(nextLecture.prerequisites_paths || []).forEach((path) => {
						const options: MultiSelectOptions = {};
						nextOptions.forEach((option) => {
							options[String(option.id)] = {
								value: option.id,
								label: option.name,
								checked: path.includes(option.id),
							};
						});
						draft.push(options);
					});
				});
				setChecked(undefined);
				setClicked(false);
			});
		} catch (error: any) {
			displaySnackbar(
				'error',
				errorMessage(error, 'Unable to load Lecture rules and paths')
			);
		}
	}, [
		displaySnackbar,
		lectureId,
		resetForm,
		setPrerequisitePaths,
		standaloneCourseId,
		withLoading,
	]);

	useEffect(() => {
		load();
	}, [load]);

	const addPath = () => {
		const options: MultiSelectOptions = {};
		lectureOptions.forEach((option) => {
			options[String(option.id)] = {
				value: option.id,
				label: option.name,
				checked: false,
			};
		});
		setPrerequisitePaths((draft) => {
			draft.push(options);
		});
		setClicked(true);
	};

	const save = async (form: RolesPathsTypes) => {
		if (form.progress_criteria === 'subset' && !form.units?.length) {
			displaySnackbar('error', 'You must select at least one Unit');
			return;
		}
		if (
			form.progress_criteria === 'percentage' &&
			(!form.progress_percentage || form.progress_percentage > 100)
		) {
			displaySnackbar('error', 'You must enter a percentage from 1 to 100');
			return;
		}
		const prerequisites = prerequisitePaths.flatMap((path, index) =>
			Object.values(path)
				.filter((option) => option.checked)
				.map((option) => ({
					path: index + 1,
					prerequisite: Number(option.value),
				}))
		);
		try {
			const response = await withLoading(() =>
				api.put(
					'standalone-courses/lectures/rules/update',
					{
						...form,
						course_id: lectureId,
						prerequisites,
					}
				)
			);
			displaySnackbar('success', response.data.message);
			await load();
		} catch (error: any) {
			displaySnackbar(
				'error',
				errorMessage(error, 'Unable to update Lecture rules and paths')
			);
		}
	};

	if (!lecture) return null;
	const watchProgress = methods.watch('progress_criteria');

	return (
		<FormProvider {...methods}>
			<form onSubmit={methods.handleSubmit(save)}>
				{/* Rules authoring is admin-only — the instructor portal reads them */}
				<GridLayout
					gridGap="2.813rem"
					gridTemplateColumns="repeat(1,1fr)"
					style={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : undefined}
				>
					<FlexLayout justifyContent="space-between" alignItems="center" width="60%">
						<label>Units order</label>
						<UnitOrderSelect />
					</FlexLayout>
					<FlexLayout justifyContent="start" alignItems="start" gridGap="2rem">
						<FlexLayout justifyContent="space-between" alignItems="center" width="60%">
							<label>Lecture completion</label>
							<CourseCompletionSelect />
						</FlexLayout>
						<div>
							{watchProgress === 'subset' && (
								<UnitSelect selectedCourse={lecture as any} />
							)}
							{watchProgress === 'percentage' && <ProgressPercentage />}
						</div>
					</FlexLayout>
					<FlexLayout justifyContent="space-between" alignItems="center" width="60%">
						<label>Prerequisite Criteria</label>
						<PrerequisiteCriteria />
					</FlexLayout>
					<FlexLayout justifyContent="space-between">
						<PrerequisiteLabel>Prerequisite</PrerequisiteLabel>
						<PrerequisiteContent flexDirection="column" margin="0 auto">
							<FlexLayout flexDirection="column" gridGap="2rem">
								{prerequisitePaths.map((path, index) => (
									<PrerequisitePath
										key={index}
										options={path}
										setOptions={setPrerequisitePaths}
										prerequisitesPaths={prerequisitePaths}
										pathIndex={index}
										name="prerequisites"
										control={methods.control}
										setChecked={setChecked}
										clicked={clicked}
									/>
								))}
							</FlexLayout>
							<Button
								color="primary"
								variant="text"
								onClick={addPath}
								disabled={checked === false}
							>
								<AddIcon /> Add an alternative rule/ prerequisite
							</Button>
						</PrerequisiteContent>
					</FlexLayout>
					{!readOnly && (
						<FlexLayout justifyContent="end" gridGap="1rem" marginTop="4rem">
							<Button
								variant="contained"
								color="warning"
								type="button"
								onClick={() =>
									history.push(
										`/dashboard/classrooms/standalone-courses/${standaloneCourseLabel}/lecture/${lectureId}/edit/content`
									)
								}
							>
								Cancel
							</Button>
							<Button
								variant="contained"
								type="submit"
								disabled={!methods.formState.isDirty}
							>
								Save
							</Button>
						</FlexLayout>
					)}
				</GridLayout>
			</form>
		</FormProvider>
	);
};

const PrerequisiteLabel = styled.div`
	width: 170px;
`;

const PrerequisiteContent = styled(FlexLayout)`
	width: 100%;
`;

export default StandaloneLectureRulesPaths;
